import React, { useMemo, useRef, useEffect, useState } from 'react';
import { sceneService } from '../services/sceneService';
import type { Scene } from '../types/scene';

interface SceneProgress extends Scene {
  status: 'upcoming' | 'current' | 'completed';
  endTime: number;
}

interface JourneyProgressProps {
  currentTime: number;
  className?: string;
}

const JourneyProgress: React.FC<JourneyProgressProps> = ({
  currentTime,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentSceneRef = useRef<HTMLDivElement>(null);
  const [previousCurrentSceneTime, setPreviousCurrentSceneTime] = useState<number | null>(null);

  const allScenes = sceneService.getAllScenes();
  
  const sceneProgressData = useMemo((): SceneProgress[] => {
    return allScenes.map((scene, index) => {
      const nextScene = allScenes[index + 1];
      const endTime = nextScene ? nextScene.time : scene.time + 60;
      
      let status: 'upcoming' | 'current' | 'completed';
      if (currentTime < scene.time) {
        status = 'upcoming';
      } else if (currentTime >= scene.time && (!nextScene || currentTime < nextScene.time)) {
        status = 'current';
      } else {
        status = 'completed';
      }

      return {
        ...scene,
        status,
        endTime
      };
    });
  }, [currentTime, allScenes]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateProgressPercentage = (): number => {
    if (sceneProgressData.length === 0) return 0;
    
    const completedScenes = sceneProgressData.filter(scene => scene.status === 'completed').length;
    const currentScene = sceneProgressData.find(scene => scene.status === 'current');
    
    let progress = completedScenes;
    
    if (currentScene) {
      const sceneProgress = Math.min(
        (currentTime - currentScene.time) / (currentScene.endTime - currentScene.time),
        1
      );
      progress += sceneProgress;
    }
    
    return (progress / sceneProgressData.length) * 100;
  };

  useEffect(() => {
    const currentScene = sceneProgressData.find(scene => scene.status === 'current');
    const currentSceneTime = currentScene?.time ?? null;

    // Only auto-scroll when the current scene actually changes (scene transition)
    const hasSceneChanged = currentSceneTime !== null && currentSceneTime !== previousCurrentSceneTime;

    if (hasSceneChanged && currentSceneRef.current && containerRef.current) {
      const container = containerRef.current;
      const currentElement = currentSceneRef.current;

      const containerHeight = container.clientHeight;
      const elementTop = currentElement.offsetTop;
      const elementHeight = currentElement.clientHeight;

      const scrollTop = elementTop - (containerHeight / 2) + (elementHeight / 2);

      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      console.log(`Auto-scrolled to scene at ${currentSceneTime}s due to scene transition`);
    }

    // Update the previous scene time reference
    setPreviousCurrentSceneTime(currentSceneTime);
  }, [sceneProgressData, previousCurrentSceneTime]);

  return (
    <div className={`journey-progress-container ${className}`}>
      <div className="journey-progress-header">
        <h3>Journey Progress</h3>
      </div>
      
      <div className="journey-progress-content" ref={containerRef}>
        <div className="timeline-container">
          <div className="timeline-line"></div>
          <div 
            className="progress-line"
            style={{ height: `${calculateProgressPercentage()}%` }}
          ></div>
          
          <div className="scene-items">
            {sceneProgressData.map((scene, index) => (
              <div
                key={`${scene.time}-${index}`}
                ref={scene.status === 'current' ? currentSceneRef : null}
                className={`scene-item ${scene.status}`}
              >
                <div className={`scene-dot ${scene.status}`}></div>
                <div className="journey-scene-info">
                  <span className={`scene-time ${scene.status}`}>
                    {formatTime(scene.time)}
                  </span>
                  <div className="scene-title-row">
                    <span className={`scene-title ${scene.status}`}>
                      {scene.title}
                    </span>
                    {scene.urls && scene.urls.length > 0 && (
                      <a 
                        href={scene.urls[0]} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="external-link-icon"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Visit external link for ${scene.title}`}
                      >
                        🔗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyProgress;